/**
 * Product Form Custom Element
 * Handles add to cart functionality with pre-order support
 * 
 * UPDATED: Now stores and maintains pre-order status from the button's data-preorder attribute
 */
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        // Get the form element and enable the variant ID input
        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        
        // Bind submit handler to this form
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        
        // Get cart notification or drawer element
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        
        // Get the submit button
        this.submitButton = this.querySelector('[type="submit"]');

        // Set aria attribute for cart drawer accessibility
        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        // Check if errors should be hidden (for gift cards)
        this.hideErrors = this.dataset.hideErrors === 'true';
        
        /**
         * UPDATED: Store the pre-order status from the button's data attribute
         * This value is set in the Liquid template based on the metafield
         * It will persist even when variants are changed via JavaScript
         */
        this.isPreorder = this.submitButton.dataset.preorder === 'true';
      }

      /**
       * Handle form submission (Add to Cart)
       * @param {Event} evt - The submit event
       */
      onSubmitHandler(evt) {
        // Prevent default form submission
        evt.preventDefault();
        
        // Don't submit if button is disabled
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Clear any previous error messages
        this.handleErrorMessage();

        // Disable button and show loading state
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        // Setup fetch configuration
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        // Prepare form data
        const formData = new FormData(this.form);
        
        // If cart exists, add sections to render
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // Submit to Shopify cart API
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            // Handle error response (product sold out, etc.)
            if (response.status) {
              // Publish cart error event
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              
              // Display error message to user
              this.handleErrorMessage(response.description);

              // Handle sold out message display
              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              // If no cart element, redirect to cart page
              window.location = window.routes.cart_url;
              return;
            }

            // Success - publish cart update event
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            
            // Handle quick add modal if present
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              // Update cart drawer/notification
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            // Remove loading state
            this.submitButton.classList.remove('loading');
            
            // Remove empty cart class if present
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            
            // Re-enable button if no errors
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            
            // Hide loading spinner
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      /**
       * Display or hide error messages
       * @param {string|boolean} errorMessage - The error message to display, or false to hide
       */
      handleErrorMessage(errorMessage = false) {
        // Don't show errors if hideErrors is true (for gift cards)
        if (this.hideErrors) return;

        // Get error message wrapper element
        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        
        // Get error message text element
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        // Toggle visibility based on whether there's an error
        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        // Set error message text if provided
        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}